import React, { SyntheticEvent, useCallback, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import Form, {
    Field,
    FormSection,
    FormFooter,
    ErrorMessage,
    Legend
} from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Box, Flex, Stack, xcss } from "@atlaskit/primitives";
import { Text } from "@atlaskit/primitives-next";
import TextField from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";

import { getFirstName } from "@/components/signup/utils";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { ANALYTICS_PREFIX } from "../../../common/constants";
import { PageProjectDetailsProps } from "../../../common/types";
import { useWizardConfiguration } from "../../../controllers";
import { useIsTeamTypeSpaceRecommendationExperiment } from "../../../hooks/experiment-hooks";

import {
    JIRA_MAX_PROJECT_NAME_LENGTH,
    JIRA_MIN_PROJECT_NAME_LENGTH,
    PROJECT_NAME_TEXT_FIELD
} from "./constants";
import messages from "./messages";
import { getTeamSpecificExamples } from "./utils";

export const LeftHandSidePageProjectDetails: React.FC<
    PageProjectDetailsProps
> = ({
    onClickNext,
    currentProjectName,
    onProjectNameChange,
    defaultProjectName,
    submitButton,
    Header
}) => {
    const { formatMessage } = useIntl();
    const loggedInUser = useLoggedInUser();
    const userName = loggedInUser.name ? getFirstName(loggedInUser.name) : "";

    const isTeamTypeSpaceRecommendationExperiment: boolean =
        useIsTeamTypeSpaceRecommendationExperiment();
    const { teamType } = useWizardConfiguration();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    // State for managing selected example button
    const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);
    const [hasClickedExample, setHasClickedExample] = useState(false);

    const handleOnSubmit = () => {
        const teamSpecificExamples = getTeamSpecificExamples(teamType);
        const selectedExampleText =
            teamSpecificExamples[selectedExampleIndex]?.defaultMessage;

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: `${ANALYTICS_PREFIX}PageProjectDetailsSubmitButton`,
            eventType: "ui",
            attributes: {
                teamType,
                isTeamTypeSpaceRecommendationExperiment,
                hasClickedExample,
                selectedExampleIndex,
                selectedExampleText,
                submittedTextMatchesExample:
                    currentProjectName === selectedExampleText
            }
        }).fire();

        onClickNext();
    };

    const handleExampleClick = useCallback(
        (exampleText: string, index: number) => {
            setSelectedExampleIndex(index);
            setHasClickedExample(true);
            onProjectNameChange(exampleText);
        },
        [onProjectNameChange]
    );

    const validateProjectName = useCallback(
        (value: string | undefined) => {
            // If the project name is undefined, we don't need to validate it since we use the default project name
            if (value === undefined) {
                return;
            }
            const trimmedValueLength = value?.trim().length ?? 0;
            if (trimmedValueLength < JIRA_MIN_PROJECT_NAME_LENGTH) {
                return formatMessage(
                    messages.projectNameMinCharacterLengthError,
                    {
                        _minCharacters: JIRA_MIN_PROJECT_NAME_LENGTH,
                        get minCharacters() {
                            return this["_minCharacters"];
                        },
                        set minCharacters(value) {
                            this["_minCharacters"] = value;
                        }
                    }
                );
            }
            if (trimmedValueLength > JIRA_MAX_PROJECT_NAME_LENGTH) {
                return formatMessage(
                    messages.projectNameMaxCharacterLengthError,
                    {
                        maxCharacters: JIRA_MAX_PROJECT_NAME_LENGTH
                    }
                );
            }
        },
        [formatMessage]
    );

    const handleProjectNameChanged = useCallback(
        (onChange: (e: SyntheticEvent<HTMLInputElement>) => void) =>
            (e: SyntheticEvent<HTMLInputElement>) => {
                onProjectNameChange(e.currentTarget.value);
                onChange(e);
            },
        [onProjectNameChange]
    );

    const FormContainer =
        checkFeatureFlagGate(featureFlags.jiraOnboardingAiMadlibV2) && Header
            ? Stack
            : FormSection;

    return (
        <Flex xcss={outerContainerStyles}>
            <Form onSubmit={handleOnSubmit}>
                {({ formProps }) => (
                    <form {...formProps} noValidate>
                        <Stack spread="space-between" xcss={containerStyles}>
                            <Stack>
                                {checkFeatureFlagGate(
                                    featureFlags.jiraOnboardingAiMadlibV2
                                ) && Header ? (
                                    <Header
                                        header={formatMessage(
                                            messages.spaceHeader
                                        )}
                                        subheader={formatMessage(
                                            messages.bodyLineOneVariation
                                        )}
                                    />
                                ) : (
                                    <Stack space="space.150">
                                        <Heading as="h1" size="large">
                                            {!isTeamTypeSpaceRecommendationExperiment ? (
                                                formatMessage(messages.title)
                                            ) : userName ? (
                                                <FormattedMessage
                                                    {...messages.titleVariation}
                                                    values={{ userName }}
                                                />
                                            ) : (
                                                formatMessage(
                                                    messages.titleVariationFallback
                                                )
                                            )}
                                        </Heading>
                                        <Box xcss={subtextStyles}>
                                            <FormattedMessage
                                                {...(!isTeamTypeSpaceRecommendationExperiment
                                                    ? messages.bodyLineOne
                                                    : messages.bodyLineOneVariation)}
                                            />
                                        </Box>
                                    </Stack>
                                )}
                                <FormContainer>
                                    <Stack space="space.300">
                                        <Field
                                            name={PROJECT_NAME_TEXT_FIELD}
                                            label={formatMessage(
                                                !isTeamTypeSpaceRecommendationExperiment
                                                    ? messages.projectFormField
                                                    : messages.projectFormFieldVariation
                                            )}
                                            validate={validateProjectName}
                                        >
                                            {({ fieldProps, error, meta }) => (
                                                <>
                                                    <TextField
                                                        {...fieldProps}
                                                        autoComplete="off"
                                                        defaultValue={
                                                            defaultProjectName
                                                        }
                                                        value={
                                                            currentProjectName
                                                        }
                                                        onChange={handleProjectNameChanged(
                                                            fieldProps.onChange
                                                        )}
                                                    />
                                                    {fieldProps.isInvalid &&
                                                        meta.touched && (
                                                            <ErrorMessage>
                                                                {error}
                                                            </ErrorMessage>
                                                        )}
                                                </>
                                            )}
                                        </Field>
                                        <Stack
                                            xcss={
                                                !isTeamTypeSpaceRecommendationExperiment
                                                    ? projectExampleStyles
                                                    : undefined
                                            }
                                            space={
                                                !isTeamTypeSpaceRecommendationExperiment
                                                    ? "space.100"
                                                    : undefined
                                            }
                                        >
                                            <Legend>
                                                {formatMessage(
                                                    !isTeamTypeSpaceRecommendationExperiment
                                                        ? messages.projectExampleHeading
                                                        : messages.projectExampleHeadingVariation
                                                )}
                                            </Legend>
                                            {!isTeamTypeSpaceRecommendationExperiment ? (
                                                // Show default string examples
                                                <Text
                                                    color="color.text.subtlest"
                                                    as="p"
                                                >
                                                    {formatMessage(
                                                        messages.projectExamples
                                                    )}
                                                </Text>
                                            ) : (
                                                // Show team-specific example buttons
                                                <Flex
                                                    gap="space.100"
                                                    wrap="wrap"
                                                    justifyContent="start"
                                                >
                                                    {getTeamSpecificExamples(
                                                        teamType
                                                    ).map((example, index) => (
                                                        <Button
                                                            key={index}
                                                            appearance="subtle"
                                                            isSelected={
                                                                selectedExampleIndex ===
                                                                index
                                                            }
                                                            onClick={() =>
                                                                handleExampleClick(
                                                                    formatMessage(
                                                                        example
                                                                    ),
                                                                    index
                                                                )
                                                            }
                                                            css={[
                                                                buttonStyles,
                                                                selectedExampleIndex ===
                                                                    index &&
                                                                    selectedButtonStyles,
                                                                selectedExampleIndex !==
                                                                    index &&
                                                                    unselectedButtonStyles
                                                            ]}
                                                        >
                                                            {formatMessage(
                                                                example
                                                            )}
                                                        </Button>
                                                    ))}
                                                </Flex>
                                            )}
                                        </Stack>
                                    </Stack>
                                </FormContainer>
                            </Stack>
                            <FormFooter>
                                {(checkFeatureFlagGate(
                                    featureFlags.jiraOnboardingAiMadlibV2
                                ) &&
                                    submitButton) || (
                                    <Button type="submit" appearance="primary">
                                        {formatMessage(messages.getStartedBtn)}
                                    </Button>
                                )}
                            </FormFooter>
                        </Stack>
                    </form>
                )}
            </Form>
        </Flex>
    );
};

const outerContainerStyles = xcss({
    height: "100%"
});
const containerStyles = xcss({
    height: "100%"
});

const subtextStyles = xcss({
    fontSize: "font.body.medium",
    color: "color.text.subtle"
});

const projectExampleStyles = xcss({
    backgroundColor: "color.background.input.hovered",
    borderRadius: "border.radius.100",
    padding: "space.100"
});

const buttonStyles = css({
    borderWidth: "1px !important",
    // eslint-disable-next-line local/ensure-wac-token-usage
    borderColor: "#BCBDC0 !important",
    borderRadius: token("border.radius", "2px") + " !important",
    borderStyle: "solid !important",
    // Hard-coded values required because token values are being overridden / ignored
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        // Match the Text component font stack (ui-sans-serif, "Segoe UI", Ubuntu...)
        fontFamily:
            "ui-sans-serif, 'Segoe UI', Ubuntu, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji' !important",
        fontSize: "14px !important",
        fontWeight: "bold !important"
    }
});

const unselectedButtonStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#626F86 !important"
    }
});

const selectedButtonStyles = css({
    borderColor: token("color.border.focused") + " !important"
});
